// @flow
import type { Action, Client, GetState } from 'state/types';
import type {
  AdminProperties,
  BackupConfiguration,
  BrandingSettings,
  DirectoryServiceDescription,
  EmailServerConfiguration,
  LoggingConfig,
  MediaEngine,
  ServerFolder,
  Ports,
  MobileUploadSettings,
} from 'state/types/serverTypes';
import {
  ADMIN_PROPERTIES_FAIL,
  ADMIN_PROPERTIES_SUCCESS,
  ADMIN_PROPERTIES,
  BACKUP,
  BACKUP_FAIL,
  BACKUP_SUCCESS,
  CANCEL_BACKUP,
  CANCEL_BACKUP_FAIL,
  CANCEL_BACKUP_SUCCESS,
  CLEAR_EMAIL_SERVER_CONFIG_VALIDATING,
  CLEAR_MEDIA_ENGINE_TEST_PENDING,
  CREATE_MEDIA_ENGINE,
  CREATE_MEDIA_ENGINE_FAIL,
  CREATE_MEDIA_ENGINE_SUCCESS,
  DELETE_MEDIA_ENGINE,
  DELETE_MEDIA_ENGINE_FAIL,
  DELETE_MEDIA_ENGINE_SUCCESS,
  GET_SERVER_FOLDERS_FAIL,
  GET_SERVER_FOLDERS_SUCCESS,
  GET_SERVER_FOLDERS,
  GET_SERVER_SYSTEM_INFORMATION,
  GET_SERVER_SYSTEM_INFORMATION_FAIL,
  GET_SERVER_SYSTEM_INFORMATION_SUCCESS,
  CREATE_DIR_SVC_DESC,
  CREATE_DIR_SVC_DESC_SUCCESS,
  CREATE_DIR_SVC_DESC_FAIL,
  DELETE_DIR_SVC_DESC,
  DELETE_DIR_SVC_DESC_SUCCESS,
  DELETE_DIR_SVC_DESC_FAIL,
  RESTART_SERVER_FAIL,
  RESTART_SERVER_SUCCESS,
  RESTART_SERVER,
  RESTORE,
  RESTORE_FAIL,
  RESTORE_SUCCESS,
  SEND_DIAGNOSTIC_DATA_FAIL,
  SEND_DIAGNOSTIC_DATA_SUCCESS,
  SEND_DIAGNOSTIC_DATA,
  SET_BACKUP_CONFIG,
  SET_BACKUP_CONFIG_FAIL,
  SET_BACKUP_CONFIG_SUCCESS,
  SET_BRANDING_IMAGE,
  SET_BRANDING_IMAGE_SUCCESS,
  SET_BRANDING_IMAGE_FAIL,
  SET_BRANDING_SETTINGS,
  SET_BRANDING_SETTINGS_FAIL,
  SET_BRANDING_SETTINGS_SUCCESS,
  SET_EMAIL_CONFIG,
  SET_EMAIL_CONFIG_FAIL,
  SET_EMAIL_CONFIG_SUCCESS,
  SET_EMAIL_SERVER_CONFIG_VALIDATING,
  SET_GLOBAL_SETTINGS,
  SET_GLOBAL_SETTINGS_FAIL,
  SET_GLOBAL_SETTINGS_SUCCESS,
  SET_MOBILE_UPLOAD_SETTINGS,
  SET_MOBILE_UPLOAD_SETTINGS_SUCCESS,
  SET_MOBILE_UPLOAD_SETTINGS_FAIL,
  SET_PORTS,
  SET_PORTS_FAIL,
  SET_PORTS_SUCCESS,
  SET_IS_COMMUNICATING_WITH_DIR_SVC,
  SET_IS_RESTARTING,
  SET_IS_ADMIN_SERVER_RESTARTING,
  SET_IS_SENDING_DIAGNOSTIC_DATA,
  SET_LOGGING_CONFIG,
  SET_LOGGING_CONFIG_FAIL,
  SET_LOGGING_CONFIG_SUCCESS,
  SET_MEDIA_ENGINE_TEST_PENDING,
  SET_SERVER_STATUS,
  SET_SSO_CREDENTIALS,
  SET_SSO_CREDENTIALS_FAIL,
  SET_SSO_CREDENTIALS_SUCCESS,
  START_SERVER_FAIL,
  START_SERVER_SUCCESS,
  START_SERVER,
  STOP_SERVER_FAIL,
  STOP_SERVER_SUCCESS,
  STOP_SERVER,
  TEST_MEDIA_ENGINE,
  TEST_MEDIA_ENGINE_FAIL,
  TEST_MEDIA_ENGINE_SUCCESS,
  UPDATE_DIR_SVC_DESC,
  UPDATE_DIR_SVC_DESC_SUCCESS,
  UPDATE_DIR_SVC_DESC_FAIL,
  UPDATE_MEDIA_ENGINE,
  UPDATE_MEDIA_ENGINE_FAIL,
  UPDATE_MEDIA_ENGINE_SUCCESS,
  UPDATE_SERVER_FOLDER_ROOT,
  UPDATE_SERVER_FOLDERS,
  VALIDATE_EMAIL_SERVER_CONFIG,
  VALIDATE_EMAIL_SERVER_CONFIG_FAIL,
  VALIDATE_EMAIL_SERVER_CONFIG_SUCCESS,
} from 'state/types/serverTypes';
import {
  API,
  ACTION_BRANDING_UPLOAD,
  BRANDING_UPLOAD_FILENAME,
} from 'util/constants';
import { PLATFORM_WINDOWS } from 'portfolio-shared/util/sharedConstants';
import {
  BACKUP_OPERATION_STATE,
  calculateDiagnosticDataSendingStatus,
  calculateIsRestarting,
} from 'util/AdminProperties';
import {
  appendToPath,
  getPathSeparator,
} from 'portfolio-shared/util/pathUtils';
import {
  getIsServerPlatformWindows,
  getServerFolderRoot,
} from 'state/selectors';
import { mergeServerFoldersResult } from 'portfolio-shared/util/folderUtils';
import { sortMediaEngines } from 'util/AdminProperties';

const initialState: AdminProperties = {
  backupConfig: {},
  backupRestoreStatus: {
    currentOperationState: BACKUP_OPERATION_STATE.NONE,
  },
  brandingSettings: {},
  emailServerConfiguration: {},
  emailConfigValidationPending: false,
  globalSettings: {},
  isCommunicatingWithDirectoryService: false,
  isRestarting: false,
  isAdminServerRestarting: false,
  isSendingDiagnosticData: false,
  loggingConfig: {},
  mediaEngineTestPending: false,
  ports: {},
  reachoutMessages: [],
  serverFolders: [],
  serverFolderRoot: '',
  serverOsPlatform: PLATFORM_WINDOWS,
};

export default function reducer(
  state: AdminProperties = initialState,
  action: Action
): AdminProperties {
  switch (action.type) {
    case ADMIN_PROPERTIES_SUCCESS:
      return {
        ...state,
        isRestarting: calculateIsRestarting(
          action.payload.serverStatus,
          state.serverStatus,
          state.isRestarting
        ),
        serverStatus: action.payload.serverStatus || state.serverStatus,
        licenses: action.payload.licenses || state.licenses,
        loggingConfig: action.payload.loggingConfig || state.loggingConfig,
        mediaEngines:
          sortMediaEngines(action.payload.mediaEngines) || state.mediaEngines,
        globalStatistics:
          action.payload.globalStatistics || state.globalStatistics,
        globalSettings: action.payload.globalSettings || state.globalSettings,
        isSendingDiagnosticData: calculateDiagnosticDataSendingStatus(
          action.payload.diagnosticDataStatus,
          state.diagnosticDataStatus,
          state.isSendingDiagnosticData
        ),
        diagnosticDataStatus:
          action.payload.diagnosticDataStatus || state.diagnosticDataStatus,
        directoryServiceDescription:
          action.payload.directoryServiceDescription ||
          state.directoryServiceDescription,
        ports: action.payload.ports || state.ports,
        backupConfig: action.payload.backupConfig || state.backupConfig,
        backupRestoreStatus:
          action.payload.backupRestoreStatus || state.backupRestoreStatus,
        reachoutMessages:
          action.payload.reachoutMessages || state.reachoutMessages,
        serverOsPlatform: action.payload.serverOsPlatform,
        emailServerConfiguration:
          action.payload.emailServerConfiguration ||
          state.emailServerConfiguration,
        brandingSettings:
          action.payload.brandingSettings || state.brandingSettings,
      };
    case SET_SERVER_STATUS:
      return {
        ...state,
        serverStatus: {
          ...state.serverStatus,
          runState: action.payload,
        },
      };
    case SET_IS_RESTARTING:
      return {
        ...state,
        isRestarting: action.payload,
      };
    case SET_IS_ADMIN_SERVER_RESTARTING:
      return {
        ...state,
        isAdminServerRestarting: action.payload,
      };
    case SET_IS_SENDING_DIAGNOSTIC_DATA:
      return {
        ...state,
        isSendingDiagnosticData: action.payload,
      };
    case GET_SERVER_SYSTEM_INFORMATION_SUCCESS:
      return {
        ...state,
        serverOsPlatform: action.payload.platform,
      };
    case UPDATE_SERVER_FOLDER_ROOT:
      return {
        ...state,
        serverFolderRoot: action.payload,
      };
    case GET_SERVER_FOLDERS_SUCCESS:
      return {
        ...state,
        serverFolders: mergeServerFoldersResult(
          action.payload,
          action.meta.path,
          state
        ),
      };
    case UPDATE_SERVER_FOLDERS:
      return {
        ...state,
        serverFolders: action.payload,
      };
    case SET_IS_COMMUNICATING_WITH_DIR_SVC:
      return {
        ...state,
        isCommunicatingWithDirectoryService: action.payload,
      };
    case SET_EMAIL_SERVER_CONFIG_VALIDATING:
      return {
        ...state,
        emailConfigValidationPending: true,
      };
    case CLEAR_EMAIL_SERVER_CONFIG_VALIDATING:
      return {
        ...state,
        emailConfigValidationPending: false,
      };
    case SET_MEDIA_ENGINE_TEST_PENDING:
      return {
        ...state,
        mediaEngineTestPending: true,
      };
    case CLEAR_MEDIA_ENGINE_TEST_PENDING:
      return {
        ...state,
        mediaEngineTestPending: false,
      };
    default:
      return state;
  }
}

function getAdminProperties(
  adminSession: ?string,
  identitySession: ?string,
  properties: Array<string>
) {
  return {
    types: [ADMIN_PROPERTIES, ADMIN_PROPERTIES_SUCCESS, ADMIN_PROPERTIES_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.get(`${API}/admin-properties/${properties.join()}`, {
        params: { adminSession, identitySession },
      });
    },
  };
}

function setServerStatus(status: string) {
  return {
    type: SET_SERVER_STATUS,
    payload: status,
  };
}

function setIsRestarting(isRestarting: boolean) {
  return {
    type: SET_IS_RESTARTING,
    payload: isRestarting,
  };
}

function setIsAdminServerRestarting(isRestarting: boolean) {
  return {
    type: SET_IS_ADMIN_SERVER_RESTARTING,
    payload: isRestarting,
  };
}

function setIsSendingDiagnosticData(isSending: boolean) {
  return {
    type: SET_IS_SENDING_DIAGNOSTIC_DATA,
    payload: isSending,
  };
}

function startServer(session: string) {
  return {
    types: [START_SERVER, START_SERVER_SUCCESS, START_SERVER_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/startServer`, {
        params: { session },
      });
    },
  };
}

function stopServer(session: string) {
  return {
    types: [STOP_SERVER, STOP_SERVER_SUCCESS, STOP_SERVER_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/stopServer`, {
        params: { session },
      });
    },
  };
}

function restartServer(session: string) {
  return {
    types: [RESTART_SERVER, RESTART_SERVER_SUCCESS, RESTART_SERVER_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/restartServer`, {
        params: { session },
      });
    },
  };
}

function sendDiagnosticData(
  session: string,
  accountName: string,
  contactName: ?string,
  techSupportTicketNum: ?string
) {
  return {
    types: [
      SEND_DIAGNOSTIC_DATA,
      SEND_DIAGNOSTIC_DATA_SUCCESS,
      SEND_DIAGNOSTIC_DATA_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/diagnosticData`, {
        params: { session },
        data: { accountName, contactName, techSupportTicketNum },
      });
    },
  };
}

function getServerSystemInformation(session: string) {
  return {
    types: [
      GET_SERVER_SYSTEM_INFORMATION,
      GET_SERVER_SYSTEM_INFORMATION_SUCCESS,
      GET_SERVER_SYSTEM_INFORMATION_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/server/system-information`, {
        params: {
          session,
        },
      }),
  };
}

function updateServerFolders(serverFolders: ServerFolder[]) {
  return {
    type: UPDATE_SERVER_FOLDERS,
    payload: serverFolders,
  };
}

function updateServerFolderRoot(rootPath: string) {
  return {
    type: UPDATE_SERVER_FOLDER_ROOT,
    payload: rootPath,
  };
}

function getServerFolders(
  session: string,
  path?: string,
  replace?: boolean,
  showFiles: boolean = false
) {
  return {
    types: [
      GET_SERVER_FOLDERS,
      GET_SERVER_FOLDERS_SUCCESS,
      GET_SERVER_FOLDERS_FAIL,
    ],
    meta: { path: replace ? '' : path },
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/folder/_contents`, {
        params: {
          session,
        },
        data: {
          path: appendToPath(
            getServerFolderRoot(getState()),
            path,
            getPathSeparator(getIsServerPlatformWindows(getState()))
          ),
          showFiles,
        },
      }),
  };
}

function createDirectoryServiceDescription(
  session: string,
  description: DirectoryServiceDescription
) {
  return {
    types: [
      CREATE_DIR_SVC_DESC,
      CREATE_DIR_SVC_DESC_SUCCESS,
      CREATE_DIR_SVC_DESC_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/directory-service`, {
        params: { session },
        data: description,
      });
    },
  };
}

function deleteDirectoryServiceDescription(session: string, uuid: string) {
  return {
    types: [
      DELETE_DIR_SVC_DESC,
      DELETE_DIR_SVC_DESC_SUCCESS,
      DELETE_DIR_SVC_DESC_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.delete(`${API}/directory-service/${uuid}`, {
        params: { session },
      });
    },
  };
}

function updateDirectoryServiceDescription(
  session: string,
  description: DirectoryServiceDescription
) {
  return {
    types: [
      UPDATE_DIR_SVC_DESC,
      UPDATE_DIR_SVC_DESC_SUCCESS,
      UPDATE_DIR_SVC_DESC_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.put(`${API}/directory-service`, {
        params: { session },
        data: description,
      });
    },
  };
}

function setBrandingCustomImage(formData: FormData, adminSession: string) {
  return {
    types: [
      SET_BRANDING_IMAGE,
      SET_BRANDING_IMAGE_SUCCESS,
      SET_BRANDING_IMAGE_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`/FileTransfer/upload`, {
        params: {
          sessionId: adminSession,
          action: ACTION_BRANDING_UPLOAD,
          filename: BRANDING_UPLOAD_FILENAME,
        },
        formData: formData,
      });
    },
  };
}

function setGlobalSettings(session: string, settings: any) {
  return {
    types: [
      SET_GLOBAL_SETTINGS,
      SET_GLOBAL_SETTINGS_SUCCESS,
      SET_GLOBAL_SETTINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/global-settings`, {
        params: { session },
        data: settings,
      });
    },
  };
}

function setMobileUploadSettings(
  adminSession: string,
  identSession: string,
  settings: MobileUploadSettings
) {
  return {
    types: [
      SET_MOBILE_UPLOAD_SETTINGS,
      SET_MOBILE_UPLOAD_SETTINGS_SUCCESS,
      SET_MOBILE_UPLOAD_SETTINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/global-settings/mobile-uploads`, {
        params: { adminSession, identSession },
        data: settings,
      });
    },
  };
}

function setLoggingConfig(adminSession: string, config: LoggingConfig) {
  return {
    types: [
      SET_LOGGING_CONFIG,
      SET_LOGGING_CONFIG_SUCCESS,
      SET_LOGGING_CONFIG_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/logging-configuration`, {
        params: { adminSession },
        data: config,
      });
    },
  };
}

function setIsCommunicatingWithDirectoryService(isCommunicating: boolean) {
  return {
    type: SET_IS_COMMUNICATING_WITH_DIR_SVC,
    payload: isCommunicating,
  };
}

function setSingleSignOnCredentials(
  identitySession: string,
  username: string,
  encryptedPassword: string
) {
  return {
    types: [
      SET_SSO_CREDENTIALS,
      SET_SSO_CREDENTIALS_SUCCESS,
      SET_SSO_CREDENTIALS_FAIL,
    ],

    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/sso`, {
        params: { identitySession },
        data: { accountName: username, encryptedPassword: encryptedPassword },
      }),
  };
}

function setEmailServerConfig(
  adminSession: string,
  config: EmailServerConfiguration
) {
  return {
    types: [SET_EMAIL_CONFIG, SET_EMAIL_CONFIG_SUCCESS, SET_EMAIL_CONFIG_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/email-server-configuration`, {
        params: { adminSession },
        data: config,
      });
    },
  };
}

function setPorts(adminSession: string, ports: Ports) {
  return {
    types: [SET_PORTS, SET_PORTS_SUCCESS, SET_PORTS_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/ports`, {
        params: { adminSession },
        data: ports,
      });
    },
  };
}

function setBackupConfig(session: string, config: BackupConfiguration) {
  return {
    types: [
      SET_BACKUP_CONFIG,
      SET_BACKUP_CONFIG_SUCCESS,
      SET_BACKUP_CONFIG_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/backup/config`, {
        params: { session },
        data: config,
      });
    },
  };
}

function setBrandingSettings(adminSession: string, config: BrandingSettings) {
  return {
    types: [
      SET_BRANDING_SETTINGS,
      SET_BRANDING_SETTINGS_SUCCESS,
      SET_BRANDING_SETTINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/branding-settings`, {
        params: { adminSession },
        data: config,
      });
    },
  };
}

function backup(session: string) {
  return {
    types: [BACKUP, BACKUP_SUCCESS, BACKUP_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/backup/_backup`, {
        params: { session },
      }),
  };
}

function cancelBackup(session: string) {
  return {
    types: [CANCEL_BACKUP, CANCEL_BACKUP_SUCCESS, CANCEL_BACKUP_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/backup/_cancel`, {
        params: { session },
      }),
  };
}

function restore(session: string, restoreArchivePath: string) {
  return {
    types: [RESTORE, RESTORE_SUCCESS, RESTORE_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/backup/_restore`, {
        params: { session },
        data: { restoreArchivePath },
      }),
  };
}

function createMediaEngine(identitySession: string, mediaEngine: MediaEngine) {
  return {
    types: [
      CREATE_MEDIA_ENGINE,
      CREATE_MEDIA_ENGINE_SUCCESS,
      CREATE_MEDIA_ENGINE_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.put(`${API}/media-engine`, {
        params: { identitySession },
        data: mediaEngine,
      });
    },
  };
}

function deleteMediaEngine(identitySession: string, mediaEngineId: string) {
  return {
    types: [
      DELETE_MEDIA_ENGINE,
      DELETE_MEDIA_ENGINE_SUCCESS,
      DELETE_MEDIA_ENGINE_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.delete(`${API}/media-engine`, {
        params: { identitySession, id: mediaEngineId },
      });
    },
  };
}

function testMediaEngine(session: string, mediaEngine: MediaEngine) {
  const { address, port } = mediaEngine;
  return {
    types: [
      TEST_MEDIA_ENGINE,
      TEST_MEDIA_ENGINE_SUCCESS,
      TEST_MEDIA_ENGINE_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.get(`${API}/media-engine/get-information`, {
        params: { session, address, port },
      });
    },
  };
}

function updateMediaEngine(identitySession: string, mediaEngine: MediaEngine) {
  return {
    types: [
      UPDATE_MEDIA_ENGINE,
      UPDATE_MEDIA_ENGINE_SUCCESS,
      UPDATE_MEDIA_ENGINE_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/media-engine`, {
        params: { identitySession },
        data: mediaEngine,
      });
    },
  };
}

function validateEmailServerConfig(
  adminSession: string,
  emailServerConfig: EmailServerConfiguration
) {
  return {
    types: [
      VALIDATE_EMAIL_SERVER_CONFIG,
      VALIDATE_EMAIL_SERVER_CONFIG_SUCCESS,
      VALIDATE_EMAIL_SERVER_CONFIG_FAIL,
    ],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/email-server-configuration/_validate`, {
        params: { adminSession },
        data: emailServerConfig,
      });
    },
  };
}

function setEmailServerConfigValidating() {
  return { type: SET_EMAIL_SERVER_CONFIG_VALIDATING };
}
function clearEmailServerConfigValidating() {
  return { type: CLEAR_EMAIL_SERVER_CONFIG_VALIDATING };
}

function setMediaEngineTestPending() {
  return { type: SET_MEDIA_ENGINE_TEST_PENDING };
}
function clearMediaEngineTestPending() {
  return { type: CLEAR_MEDIA_ENGINE_TEST_PENDING };
}

const actions = {
  backup,
  cancelBackup,
  clearEmailServerConfigValidating,
  clearMediaEngineTestPending,
  createDirectoryServiceDescription,
  createMediaEngine,
  deleteDirectoryServiceDescription,
  deleteMediaEngine,
  getAdminProperties,
  getServerFolders,
  getServerSystemInformation,
  setSingleSignOnCredentials,
  restartServer,
  restore,
  sendDiagnosticData,
  setBackupConfig,
  setBrandingCustomImage,
  setBrandingSettings,
  setEmailServerConfig,
  setEmailServerConfigValidating,
  setGlobalSettings,
  setMobileUploadSettings,
  setIsCommunicatingWithDirectoryService,
  setIsRestarting,
  setIsAdminServerRestarting,
  setIsSendingDiagnosticData,
  setLoggingConfig,
  setMediaEngineTestPending,
  setPorts,
  setServerStatus,
  startServer,
  stopServer,
  testMediaEngine,
  updateDirectoryServiceDescription,
  updateMediaEngine,
  updateServerFolders,
  updateServerFolderRoot,
  validateEmailServerConfig,
};

export { actions };



// WEBPACK FOOTER //
// ./src/state/reducers/serverReducer.js